import React, { useState, useEffect } from 'react';
import { notifyCartUpdated } from '../../utils/cartEvents';
import { useHistory, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, AlertCircle, X, Download, Trash2, Plus, Minus, ShoppingBag, Loader, Package, ArrowLeft } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import * as XLSX from 'xlsx';
import UserLayout from './UserLayout';
import PageHeader from '../shared/PageHeader';
import Card from '../shared/Card';
import Button from '../shared/Button';
import { showNotification, showError } from '../../utils/notificationHandler';
import { formatCurrency } from '../../utils/currency';
import { useOrderingEnabled } from '../../hooks/useOrderingEnabled';

const LoadingOverlay = ({ message }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center">
      <Loader className="w-12 h-12 text-brand animate-spin" />
      <p className="mt-4 text-lg font-semibold text-gray-900">{message}</p>
    </div>
  </div>
);

const OrderReviewItem = ({ item, index, onQuantityChange, onRemoveLine }) => {
  const handleQuantityChange = (newQuantity) => {
    onQuantityChange(index, newQuantity !== null ? Math.max(1, newQuantity) : null);
  };

  return (
    <div className={`flex flex-col lg:flex-row lg:items-center lg:justify-between py-4 border-b border-gray-200 last:border-b-0 ${
      !item.isFound ? 'bg-red-50' : item.isAlternative ? 'bg-orange-50' : ''
    }`}>
      <div className="flex items-start space-x-4 flex-1">
        <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <img 
            src={item.SmallImageUrl}
            alt={item.PartNumber}
            className="w-14 h-14 object-contain rounded"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://syd1.digitaloceanspaces.com/cdn-assets.mhs/images/placeholders/mhs_placeholder.png';
            }}
          />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">{item.PartNumber}</h3>
          {item.isAlternative && (
            <p className="text-sm text-orange-600 font-medium">
              Alternative for: {item.PrimaryPartNumber}
            </p>
          )}
          <p className={`text-sm mt-1 ${item.isFound ? 'text-gray-600' : 'text-red-600 font-semibold'}`}>
            {item.Description}
          </p>
          {item.isFound ? (
            <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
              <span>Price: <span className="font-medium tabular-nums">{item.Price ? formatCurrency(item.Price) : 'N/A'}</span></span>
              <span>Available: <span className="font-medium">{item.QuantityAvailable}</span></span>
            </div>
          ) : (
            <p className="text-sm text-red-600 font-medium mt-1">Part number not found</p>
          )}
        </div>
      </div>
      
      <div className="flex items-center space-x-4 mt-4 lg:mt-0">
        {item.isFound && (
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium text-gray-700">Qty:</span>
            <div className="flex items-center border border-gray-300 rounded-lg">
              <button
                onClick={() => handleQuantityChange((item.Quantity || 0) - 1)}
                className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 transition-colors duration-200 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand"
                disabled={!item.isFound}
                aria-label="Decrease quantity"
              >
                <Minus size={16} />
              </button>
              <input
                type="number"
                value={item.Quantity || ''}
                onChange={(e) => handleQuantityChange(e.target.value === '' ? null : parseInt(e.target.value, 10))}
                className="w-16 text-center py-2 border-0 tabular-nums focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand text-gray-900 font-medium"
                min="1"
                disabled={!item.isFound}
                aria-label="Quantity"
              />
              <button
                onClick={() => handleQuantityChange((item.Quantity || 0) + 1)}
                className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 transition-colors duration-200 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand"
                disabled={!item.isFound}
                aria-label="Increase quantity"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>
        )}
        
        <Button
          variant="ghostDanger"
          onClick={() => onRemoveLine(index)}
          ariaLabel={`Remove ${item.PartNumber}`}
          icon={<Trash2 className="w-5 h-5" aria-hidden="true" />}
        />
      </div>
    </div>
  );
};

const UserBulkUpload = () => {
  const { enabled: orderingEnabled, message: orderingMessage } = useOrderingEnabled();
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadedData, setUploadedData] = useState([]);
  const [isReviewing, setIsReviewing] = useState(false);
  const [accountId, setAccountId] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const history = useHistory();
  const location = useLocation();

  const placeholderImageUrl = "https://syd1.digitaloceanspaces.com/cdn-assets.mhs/images/placeholders/mhs_placeholder.png";

  useEffect(() => {
    const fetchAccountId = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data, error } = await supabase
          .from('accounts')
          .select('id')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error fetching account ID:', error);
        } else {
          setAccountId(data.id);
        }
      }
    };

    fetchAccountId();
  }, []);

  // Lines pasted into the dashboard Quick Order box arrive via router state and
  // skip the file step. Needs accountId for customer pricing, so wait for it.
  useEffect(() => {
    const lines = location.state?.quickOrderLines;
    if (!accountId || !lines?.length) return;
    // Clear the state first so a refresh or back-navigation doesn't re-run it.
    history.replace(location.pathname, {});
    (async () => {
      setLoading(true);
      try {
        const enrichedOrders = await enrichOrderData(lines);
        setUploadedData(enrichedOrders);
        setIsReviewing(true);
      } catch (error) {
        showError(error, 'Failed to look up the pasted parts');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    if (file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' &&
        file.type !== 'application/vnd.ms-excel' &&
        file.type !== 'text/csv') {
          showNotification('Please upload a valid Excel or CSV file', 'error');
      return;
    }
    setFile(file);
  };

  const handleUpload = async () => {
    if (!file) {
      showNotification('Please select a file to upload', 'error');
      return;
    }

    setLoading(true);

    try {
      const data = await readFileAsync(file);
      const orders = parseFileData(data);
      const enrichedOrders = await enrichOrderData(orders);
      setUploadedData(enrichedOrders);
      setIsReviewing(true);
      showNotification('File uploaded and processed successfully. Please review your order.', 'success');
    } catch (error) {
      console.error('Error processing file:', error);
      showError(error, 'Failed to process file. Please check the file and try again.');
    } finally {
      setLoading(false);
    }
  };

  const readFileAsync = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = e.target.result;
        resolve(data);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsBinaryString(file);
    });
  };

  const parseFileData = (data) => {
    const workbook = XLSX.read(data, { type: 'binary' });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    return XLSX.utils.sheet_to_json(sheet, { defval: null });
  };

  const enrichOrderData = async (orders) => {
    const enrichedOrders = [];
    setIsSearching(true);
    for (const order of orders) {
      if (!order.PartNumber) {
        console.error('Skipping row without PartNumber');
        continue;
      }
      try {
        const response = await fetch('/api/enquire/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            partNumber: order.PartNumber,
            account_id: accountId
          }),
        });
  
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
  
        const data = await response.json();
        console.log('Full API response for', order.PartNumber, ':', JSON.stringify(data, null, 2));
  
        if (data.cross_references && data.cross_references.length > 0) {
          const primaryPart = data.cross_references[0];
          enrichedOrders.push({
            PartNumber: order.PartNumber,
            PrimaryPartNumber: primaryPart.prod_code,
            Description: primaryPart.main_description,
            Quantity: order.Quantity || null,
            Price: primaryPart.buy_price || primaryPart.base_price || null,
            QuantityAvailable: primaryPart.qty_available || 0,
            SmallImageUrl: primaryPart.small_image_url || placeholderImageUrl,
            isAlternative: true,
            isFound: true
          });
        } else if (data.prod_code || data.bom_code) {
          enrichedOrders.push({
            PartNumber: order.PartNumber,
            Description: data.Description || 'N/A',
            Quantity: order.Quantity || null,
            Price: data.buy_price || data.base_price || null,
            QuantityAvailable: data.qty_available || 0,
            SmallImageUrl: data.small_image_url || placeholderImageUrl,
            isAlternative: false,
            isFound: true
          });
        } else {
          console.error('Unexpected API response structure:', data);
          throw new Error('Unexpected API response structure');
        }
      } catch (error) {
        console.error(`Error fetching details for ${order.PartNumber}:`, error);
        enrichedOrders.push({
          PartNumber: order.PartNumber,
          Description: 'Part number not found',
          Quantity: order.Quantity || null,
          Price: null,
          QuantityAvailable: 0,
          SmallImageUrl: placeholderImageUrl,
          isAlternative: false,
          isFound: false
        });
      }
    }
    setIsSearching(false);
    return enrichedOrders;
  };

  const handleQuantityChange = (index, newQuantity) => {
    const updatedData = [...uploadedData];
    updatedData[index].Quantity = newQuantity !== null ? Math.max(1, newQuantity) : null;
    setUploadedData(updatedData);
  };

  const handleRemoveLine = (index) => {
    const updatedData = uploadedData.filter((_, i) => i !== index);
    setUploadedData(updatedData);
  };

  const handleProceedToCheckout = () => {
    if (!orderingEnabled) {
      showNotification(orderingMessage, 'error');
      return;
    }
    const cartItems = uploadedData.filter(item => item.isFound && item.Quantity && item.Quantity > 0).map(item => ({
      partNumber: item.PartNumber,
      description: item.Description,
      quantity: item.Quantity,
      price: item.Price || 0, // Default to 0 if price is null/undefined
    }));

    if (cartItems.length === 0) {
      showNotification('Please add quantities to at least one valid item before proceeding to checkout.', 'error');
      return;
    }

    // Add to existing cart instead of replacing
    const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
    const mergedCart = [...existingCart];
    
    cartItems.forEach(newItem => {
      const existingIndex = mergedCart.findIndex(item => item.partNumber === newItem.partNumber);
      if (existingIndex >= 0) {
        mergedCart[existingIndex].quantity += newItem.quantity;
      } else {
        mergedCart.push(newItem);
      }
    });

    localStorage.setItem('cart', JSON.stringify(mergedCart));
    notifyCartUpdated();
    showNotification(`${cartItems.length} items added to cart successfully`, 'success');
    history.push('/user/cart');
  };

  const downloadTemplate = (format) => {
    const data = [
      { PartNumber: 'ABC123', Quantity: 10, Description: 'Sample Part 1', Price: 9.99 },
      { PartNumber: 'XYZ789', Quantity: 5, Description: 'Sample Part 2', Price: 14.99 }
    ];

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Orders");

    if (format === 'xlsx') {
      XLSX.writeFile(wb, "bulk_order_template.xlsx");
    } else if (format === 'csv') {
      XLSX.writeFile(wb, "bulk_order_template.csv", { bookType: "csv" });
    }
  };

  const getOrderSummary = () => {
    const validItems = uploadedData.filter(item => item.isFound && item.Quantity && item.Quantity > 0);
    const totalItems = validItems.reduce((sum, item) => sum + item.Quantity, 0);
    const totalValue = validItems.reduce((sum, item) => sum + (item.Price * item.Quantity), 0);
    return { validItems: validItems.length, totalItems, totalValue };
  };

  return (
    <UserLayout>
      <div className="space-y-6">
        <PageHeader
          title="Bulk Order Upload"
          description="Upload a spreadsheet of part numbers and quantities, review the matches, then add them to your cart."
          actions={isReviewing && (
            <Button
              variant="secondary"
              onClick={() => setIsReviewing(false)}
              icon={<ArrowLeft className="w-4 h-4" aria-hidden="true" />}
            >
              Back to Upload
            </Button>
          )}
        />

        {!isReviewing ? (
          <>
            {/* File Upload Section */}
            <Card header={<h2 className="text-lg font-semibold text-gray-900">Upload Your Order File</h2>}>
              <div
                className={`border-2 border-dashed rounded-lg p-10 text-center transition-colors duration-200 ${
                  dragging ? 'border-brand bg-brand/10' : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  id="file-upload"
                  className="hidden"
                  onChange={handleFileChange}
                  accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer flex flex-col items-center justify-center"
                >
                  <Upload className="w-12 h-12 text-gray-400 mb-4" />
                  <p className="text-lg font-semibold text-gray-700 mb-2">
                    {file ? file.name : 'Drag and drop your file here'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {file ? 'Click to change file' : 'or click to select a CSV or Excel file'}
                  </p>
                </label>
              </div>
              
              {file && (
                <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg mt-4">
                  <div className="flex items-center space-x-3">
                    <Package className="w-5 h-5 text-gray-600" />
                    <span className="text-sm font-medium text-gray-700">{file.name}</span>
                    <span className="text-xs text-gray-500">({(file.size / 1024).toFixed(1)} KB)</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFile(null)}
                    ariaLabel="Remove file"
                    icon={<X className="w-4 h-4" aria-hidden="true" />}
                  />
                </div>
              )}

              <div className="mt-6">
                <Button
                  size="lg"
                  onClick={handleUpload}
                  disabled={!file}
                  loading={loading}
                  className="w-full sm:w-auto font-semibold"
                  icon={<Upload className="w-5 h-5" aria-hidden="true" />}
                >
                  {loading ? 'Processing...' : 'Upload and Review'}
                </Button>
              </div>
            </Card>

            {/* Template Downloads */}
            <Card header={<h2 className="text-lg font-semibold text-gray-900">Download Templates</h2>}>
              <p className="text-sm text-gray-600 mb-4">Use these templates to ensure your bulk upload is formatted correctly:</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="secondary"
                  onClick={() => downloadTemplate('xlsx')}
                  icon={<Download className="w-4 h-4" aria-hidden="true" />}
                >
                  Excel Template
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => downloadTemplate('csv')}
                  icon={<Download className="w-4 h-4" aria-hidden="true" />}
                >
                  CSV Template
                </Button>
              </div>
            </Card>

            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start">
                <AlertCircle className="flex-shrink-0 h-5 w-5 text-blue-600 mt-0.5" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">Important Notes:</h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <ul className="list-disc pl-5 space-y-1">
                      <li>File must be in CSV or Excel format</li>
                      <li>Required column: PartNumber</li>
                      <li>Optional column: Quantity</li>
                      <li>Description and Price will be fetched from our database</li>
                      <li>If Quantity is not provided, you can enter it during review</li>
                      <li>Maximum 1000 lines per upload</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Order Review Section */}
            <Card
              header={
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Review Your Order</h2>
                  {(() => {
                    const summary = getOrderSummary();
                    return (
                      <div className="text-sm text-gray-600 tabular-nums">
                        {summary.validItems} valid items • {summary.totalItems} total quantity
                        {summary.totalValue > 0 && (
                          <span> • Est. {formatCurrency(summary.totalValue)}</span>
                        )}
                      </div>
                    );
                  })()}
                </div>
              }
            >
              <div className="space-y-0">
                {uploadedData.map((item, index) => (
                  <OrderReviewItem
                    key={index}
                    item={item}
                    index={index}
                    onQuantityChange={handleQuantityChange}
                    onRemoveLine={handleRemoveLine}
                  />
                ))}
              </div>
              
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
                <Button variant="secondary" size="lg" onClick={() => setIsReviewing(false)}>
                  Back to Upload
                </Button>
                {orderingEnabled && (
                  <Button
                    size="lg"
                    onClick={handleProceedToCheckout}
                    className="font-semibold"
                    icon={<ShoppingBag className="w-5 h-5" aria-hidden="true" />}
                  >
                    Add to Cart & Continue
                  </Button>
                )}
              </div>
            </Card>
          </>
        )}

        {/* Processing Overlay */}
        <AnimatePresence>
          {isSearching && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <LoadingOverlay message="Processing parts..." />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </UserLayout>
  );
};

export default UserBulkUpload;
